// ---------------------------------------------------------------------------
// MKT-008 / F-110 / F-150 / BIL-017 — Stripe Connect accounts table
// ---------------------------------------------------------------------------
// Per-tenant connected Stripe accounts (marketplace revenue share + payouts).
//
// Stories closed:
//   - F-110 Checkout: per-tenant connected accounts for KYC onboarding
//   - F-150 Marketplace: split-payments destination_account
//   - MKT-008 Marketplace payouts: payouts_enabled gate
//   - BIL-017 Payout settlement: stripe_connect provider rows
//
// One row per (tenant_id, stripe_account_id). Tenants MAY have multiple
// (express + standard side-by-side) for franchise/agency models, so the
// primary key is `id` not `(tenant_id, account_type)`. Uniqueness is
// enforced on `stripe_account_id` since Stripe IDs are global.
//
// No PII in this table — KYC details live in Stripe. We persist only the
// account-id, the capability flags (charges_enabled, payouts_enabled), and
// the requirements vector so the dashboard can show "complete onboarding"
// vs "ready to receive payouts".
// ---------------------------------------------------------------------------

import {
  pgTable,
  uuid,
  text,
  boolean,
  timestamp,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

// DB-side enum mirror of the plugin's account-type union. Kept in sync with
// `packages/payments/src/plugins/stripe-connect.ts`. The plugin parser refuses
// to coerce unknown values into `express` — see InvalidStripeAccountTypeError.
export const STRIPE_CONNECT_ACCOUNT_TYPES = [
  "express",
  "standard",
  "custom",
] as const;
export type StripeConnectAccountType =
  (typeof STRIPE_CONNECT_ACCOUNT_TYPES)[number];

/**
 * Subset of Stripe `requirements.disabled_reason` strings we treat as
 * actionable in the operator dashboard. Stripe may add more — store the
 * raw text so unknown reasons round-trip without information loss.
 */
export const STRIPE_CONNECT_DISABLED_REASONS = [
  "requirements.past_due",
  "requirements.pending_verification",
  "rejected.fraud",
  "rejected.terms_of_service",
  "rejected.listed",
  "rejected.other",
  "listed",
  "under_review",
  "platform_paused",
  "action_required.requested_capabilities",
] as const;

export type StripeConnectDisabledReason =
  (typeof STRIPE_CONNECT_DISABLED_REASONS)[number];

/**
 * Stripe `requirements` summary shape. We mirror the subset of fields
 * the platform reads — full vector is preserved in `requirements_raw`.
 */
export interface StripeConnectRequirementsSnapshot {
  currentlyDue: string[];
  eventuallyDue: string[];
  pastDue: string[];
  pendingVerification: string[];
  disabledReason: string | null;
}

export const stripeConnectAccounts = pgTable(
  "stripe_connect_accounts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    stripeAccountId: text("stripe_account_id").notNull(),
    accountType: text("account_type").notNull(), // express | standard
    country: text("country").notNull(), // ISO 3166-1 alpha-2 (e.g. "US", "RO")
    defaultCurrency: text("default_currency").notNull(), // lowercase, e.g. "eur"
    email: text("email"), // contact email submitted to Stripe (no PII beyond what tenant typed)
    businessName: text("business_name"),
    chargesEnabled: boolean("charges_enabled").default(false).notNull(),
    payoutsEnabled: boolean("payouts_enabled").default(false).notNull(),
    detailsSubmitted: boolean("details_submitted").default(false).notNull(),
    requirementsCurrentlyDue: jsonb(
      "requirements_currently_due",
    ).$type<string[]>().default([]).notNull(),
    requirementsEventuallyDue: jsonb(
      "requirements_eventually_due",
    ).$type<string[]>().default([]).notNull(),
    requirementsPastDue: jsonb(
      "requirements_past_due",
    ).$type<string[]>().default([]).notNull(),
    requirementsPendingVerification: jsonb(
      "requirements_pending_verification",
    ).$type<string[]>().default([]).notNull(),
    requirementsDisabledReason: text("requirements_disabled_reason"),
    requirementsRaw: jsonb("requirements_raw"), // full Stripe payload for forensic re-parse
    metadata: jsonb("metadata").$type<Record<string, string>>().default({}).notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_stripe_connect_account_id").on(table.stripeAccountId),
    index("idx_stripe_connect_tenant").on(table.tenantId),
    index("idx_stripe_connect_payouts_enabled").on(
      table.tenantId,
      table.payoutsEnabled,
    ),
  ],
);

export type StripeConnectAccountRow = typeof stripeConnectAccounts.$inferSelect;
export type StripeConnectAccountInsert =
  typeof stripeConnectAccounts.$inferInsert;
